import React, { useRef, useState, useMemo, useEffect, useCallback } from "react";
import { Virtuoso } from "react-virtuoso";
import MessageItem from "./MessageItem";
import { buildQuotedLookupIndex, isWhatsappAppOutboundFromMessage } from "../../utils/messageThreadHints";
import { Fab, Fade } from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import DateSeparator from "./shared/DateSeparator";
import ChannelSessionMarker from "./shared/ChannelSessionMarker";
import ActivityLogMessage from "./messages/ActivityLogMessage";
import ActivityLogGroup from "./messages/ActivityLogGroup";
import { ActivityTaskModalProvider } from "./ActivityTaskModal";
import {
  buildTimelineItems,
  sortMessagesChronologically,
  getActivityExpandPreference,
  getInternalNoteCluster,
} from "./timelineUtils";
import { nestInboundEmailAttachments } from "./emailInboundAttachments";
import { isInternalNoteType, isMessageFromLoggedInUser } from "./messageTypeRegistry";

const INITIAL_FIRST_ITEM_INDEX = 100_000;
const TIMELINE_BOTTOM_SPACER_PX = 20;

const TimelineListFooter = () => <div style={{ height: TIMELINE_BOTTOM_SPACER_PX }} />;

/** Alinhamento: cliente → esquerda; qualquer usuário (logado ou outro atendente) → direita. */
const getMessageAlignment = (message, loggedUser) => {
  if (!message) return "flex-end";
  const senderType = (message?.senderType || "").toLowerCase();
  const sender = (message?.sender || "").toLowerCase();
  const isWhatsappAppOutbound = isWhatsappAppOutboundFromMessage(message);
  const isClient =
    !isWhatsappAppOutbound &&
    (sender === "cliente" ||
      sender === "client" ||
      senderType === "client" ||
      senderType === "customer" ||
      message?.channel === "customer");
  const loggedUserId = loggedUser?.id != null ? String(loggedUser.id) : null;
  const msgUserId =
    message?.senderId != null
      ? String(message.senderId)
      : message?.id_usuario != null
        ? String(message.id_usuario)
        : null;
  const isAgentOrOperator =
    sender === "agent" ||
    sender === "operator" ||
    senderType === "agent" ||
    senderType === "operator" ||
    senderType === "ai_agent" ||
    senderType === "chatbot";
  const isUser =
    !!loggedUser &&
    (isAgentOrOperator || (!!loggedUserId && !!msgUserId && msgUserId === loggedUserId));
  if (message.type === "log") return "flex-start";
  if (isInternalNoteType(message)) {
    return isMessageFromLoggedInUser(message, loggedUser) ? "flex-end" : "flex-start";
  }
  if (isWhatsappAppOutbound) return "flex-end";
  if (isUser) return "flex-end";
  return "flex-start";
};

const getTimelineItemKey = (item) => {
  if (!item) return "empty";
  if (item.kind === "dateSeparator" || item.kind === "channelMarker") return item.id;
  if (item.kind === "logGroup") return item.id;
  if (item.kind === "log" || item.kind === "message") {
    return `${item.kind}-${item.message?.id ?? "unknown"}`;
  }
  return String(item.id ?? item.kind ?? "item");
};

const MessageList = ({
  messages = [],
  loggedUser,
  onSendMessage,
  scrollBehavior = "smooth",
  defaultChannel,
  conversationChannelId = null,
  conversationChannelLabel = null,
  personalThreadClientLabel = null,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
  onDeleteMessage,
  onReactMessage,
  onEditWhatsappMessage,
  onForwardWhatsappMessage,
  onQuoteWhatsappMessage,
  onEditNote,
  onDeleteNote,
  conversationProjectId = null,
  selectedConversation = null,
}) => {
  const virtuosoRef = useRef(null);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [initialAutoScrollDone, setInitialAutoScrollDone] = useState(false);
  const [firstItemIndex, setFirstItemIndex] = useState(INITIAL_FIRST_ITEM_INDEX);
  const timelineSnapshotRef = useRef({ firstId: null, len: 0 });
  const activityDefaultExpanded = getActivityExpandPreference();
  const listKey = selectedConversation?.id ?? null;

  const orderedMessages = useMemo(
    () => sortMessagesChronologically(messages),
    [messages]
  );

  const displayMessages = useMemo(
    () => nestInboundEmailAttachments(orderedMessages),
    [orderedMessages]
  );

  const timelineItems = useMemo(
    () =>
      buildTimelineItems(displayMessages, {
        channelId: conversationChannelId,
        channelLabel: conversationChannelLabel,
      }),
    [displayMessages, conversationChannelId, conversationChannelLabel]
  );

  const quotedLookup = useMemo(() => buildQuotedLookupIndex(orderedMessages), [orderedMessages]);

  useEffect(() => {
    setFirstItemIndex(INITIAL_FIRST_ITEM_INDEX);
    setInitialAutoScrollDone(false);
    setIsAtBottom(true);
    timelineSnapshotRef.current = { firstId: null, len: 0 };
  }, [listKey]);

  // Keep Virtuoso scroll anchored when older pages are prepended (startReached).
  useEffect(() => {
    const firstId = orderedMessages[0]?.id ?? null;
    const firstNumeric = Number(firstId);
    const len = timelineItems.length;
    const snap = timelineSnapshotRef.current;
    const snapNumeric = Number(snap.firstId);

    if (
      Number.isFinite(firstNumeric) &&
      Number.isFinite(snapNumeric) &&
      firstNumeric < snapNumeric &&
      len > snap.len
    ) {
      setFirstItemIndex((idx) => idx - (len - snap.len));
    }

    timelineSnapshotRef.current = { firstId, len };
  }, [orderedMessages, timelineItems.length]);

  const scrollToMessageId = useCallback(
    (targetId) => {
      const tid = Number(targetId);
      if (!Number.isFinite(tid)) return;
      const idx = timelineItems.findIndex((item) => {
        if (item.kind === "message" || item.kind === "log") {
          return Number(item.message?.id) === tid;
        }
        if (item.kind === "logGroup") {
          return item.messages.some((m) => Number(m?.id) === tid);
        }
        return false;
      });
      if (idx < 0 || !virtuosoRef.current) return;
      virtuosoRef.current.scrollToIndex({
        index: idx,
        align: "center",
        behavior: "smooth",
      });
    },
    [timelineItems]
  );

  const scrollToBottom = (behavior = "smooth") => {
    if (virtuosoRef.current) {
      virtuosoRef.current.scrollToIndex({
        index: Math.max(0, timelineItems.length - 1),
        align: "end",
        behavior,
      });
    }
  };

  useEffect(() => {
    if (!initialAutoScrollDone && timelineItems.length > 0) {
      let cancelled = false;
      const id1 = requestAnimationFrame(() => {
        if (cancelled) return;
        requestAnimationFrame(() => {
          if (cancelled) return;
          scrollToBottom("auto");
          setInitialAutoScrollDone(true);
        });
      });
      return () => {
        cancelled = true;
        cancelAnimationFrame(id1);
      };
    }
  }, [timelineItems.length, initialAutoScrollDone, listKey]);

  const renderTimelineItem = (item) => {
    if (item.kind === "dateSeparator") {
      return (
        <div style={{ width: "100%" }}>
          <DateSeparator label={item.label} />
        </div>
      );
    }

    if (item.kind === "channelMarker") {
      return (
        <div style={{ width: "100%" }}>
          <ChannelSessionMarker channelLabel={item.channelLabel} />
        </div>
      );
    }

    if (item.kind === "logGroup") {
      return (
        <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
          <ActivityLogGroup
            messages={item.messages}
            defaultExpanded={activityDefaultExpanded}
            loggedUser={loggedUser}
            leadId={selectedConversation?.id ?? null}
          />
        </div>
      );
    }

    if (item.kind === "log") {
      return (
        <div style={{ width: "100%", display: "flex", justifyContent: "center" }}>
          <ActivityLogMessage
            message={item.message}
            defaultExpanded={activityDefaultExpanded}
            loggedUser={loggedUser}
            leadId={selectedConversation?.id ?? null}
          />
        </div>
      );
    }

    const message = item.message;
    const justifyContent = getMessageAlignment(message, loggedUser);
    const messageIndex = orderedMessages.findIndex(
      (m) => m?.id != null && message?.id != null && String(m.id) === String(message.id)
    );
    const internalNoteCluster =
      messageIndex >= 0
        ? getInternalNoteCluster(orderedMessages, messageIndex, loggedUser)
        : undefined;

    return (
      <div
        style={{
          width: "100%",
          display: "flex",
          justifyContent,
        }}
      >
        <MessageItem
          message={message}
          user={loggedUser}
          onSendMessage={onSendMessage}
          defaultChannel={defaultChannel}
          conversationChannelId={conversationChannelId}
          conversationChannelLabel={conversationChannelLabel}
          personalThreadClientLabel={personalThreadClientLabel}
          quotedLookup={quotedLookup}
          onNavigateToQuotedMessage={scrollToMessageId}
          onDeleteMessage={onDeleteMessage}
          onReactMessage={onReactMessage}
          onEditWhatsappMessage={onEditWhatsappMessage}
          onForwardWhatsappMessage={onForwardWhatsappMessage}
          onQuoteWhatsappMessage={onQuoteWhatsappMessage}
          onEditNote={onEditNote}
          onDeleteNote={onDeleteNote}
          conversationMessages={orderedMessages}
          conversationProjectId={conversationProjectId}
          internalNoteCluster={internalNoteCluster}
          selectedConversation={selectedConversation}
        />
      </div>
    );
  };

  return (
    <ActivityTaskModalProvider leadId={selectedConversation?.id ?? null}>
    <div style={{ position: "relative", flexGrow: 1, height: "100%" }}>
      <Virtuoso
        key={listKey}
        ref={virtuosoRef}
        data={timelineItems}
        firstItemIndex={firstItemIndex}
        computeItemKey={(_index, item) => getTimelineItemKey(item)}
        followOutput={(isBottom) => (isBottom ? scrollBehavior : false)}
        components={{ Footer: TimelineListFooter }}
        startReached={() => {
          if (hasMore && !isLoadingMore && typeof onLoadMore === "function") {
            onLoadMore();
          }
        }}
        itemContent={(_index, item) => renderTimelineItem(item)}
        atBottomStateChange={setIsAtBottom}
        style={{ height: "100%", width: "100%" }}
      />

      <Fade in={!isAtBottom}>
        <Fab
          color="primary"
          size="small"
          onClick={() => scrollToBottom("smooth")}
          style={{
            position: "absolute",
            bottom: 16,
            right: 16,
            zIndex: 10,
          }}
        >
          <ArrowDownwardIcon />
        </Fab>
      </Fade>
    </div>
    </ActivityTaskModalProvider>
  );
};

export default MessageList;
